import { twMerge } from "tailwind-merge";

import { BaseButton, type BaseButtonProps } from "mds/components/buttons/BaseButton";
import {
  BUTTON_BASE_CLASSES,
  BUTTON_DISABLED_WITHOUT_BORDER_CLASSES,
} from "mds/components/buttons/helpers";
import type { PolymorphicRef } from "types";

const COMMON_CLASSES = twMerge(
  BUTTON_BASE_CLASSES,
  BUTTON_DISABLED_WITHOUT_BORDER_CLASSES,
  "px-3 py-1 h-[2.125rem] text-sm font-medium rounded-3xl",
);

const PILL_BANNER_BUTTON_KIND_CLASSES = {
  // The names of these button kinds correspond to the color of the banner itself, which is why there are some duplicates
  "banner-black": "text-black-tint-20 bg-white border border-green-shade-40",
  "banner-green": "text-black-tint-20 bg-white border border-green-shade-40",
  "banner-light-green": "text-black-tint-20 bg-white border border-green-tint-20",
  "banner-orange": "text-black-tint-20 bg-white border border-orange-shade-20",
  "banner-light-gray": "text-black-tint-20 bg-white border border-black-tint-40",
  "banner-dark-gray": "text-black-tint-20 bg-white border border-black-tint-40",
  "banner-blue": "text-white bg-blue-shade-40 border border-blue hover:bg-blue-shade-40",
  "banner-hibiscus": "text-black-tint-20 bg-white border border-hibiscus-tint-55",

  // These button kinds are copied over from the soon-to-be-deprecated `PillButton` component
  blue: "text-white bg-blue-shade-20 hover:bg-blue hover:text-white active:bg-blue-shade-40",
  green: "text-white bg-green-shade-20 hover:bg-green active:bg-green-shade-40",
  white:
    "border border-black-tint-90 bg-white text-black-tint-40 hover:bg-black-tint-90 hover:text-black-tint-20 active:bg-black-tint-97",
};

type PillBannerButtonKind = keyof typeof PILL_BANNER_BUTTON_KIND_CLASSES;

export type PillBannerButtonProps<T extends React.ElementType = "button"> = BaseButtonProps<T> & {
  kind?: PillBannerButtonKind;
};

// This button should only be used inside a PillBanner (not part of MDS).
export const PillBannerButton = function PillBannerButtonInternal({
  ref,
  children,
  kind,
  className,
  ...rest
}: Omit<PillBannerButtonProps<"button">, "ref"> & { ref?: React.Ref<HTMLButtonElement> }) {
  return (
    <BaseButton
      className={twMerge(
        COMMON_CLASSES,
        kind ? PILL_BANNER_BUTTON_KIND_CLASSES[kind] : "",
        className,
      )}
      ref={ref}
      {...rest}
    >
      {children}
    </BaseButton>
  );
} as <T extends React.ElementType = "button">(
  props: PillBannerButtonProps<T> & { ref?: PolymorphicRef<T> },
) => React.ReactNode;
